import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const MobileMenu = ({ isOpen, onClose, onSignUp, onLogin, onStats, onReset, stats, handsPlayed }) => {
  const { t } = useLanguage();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      {/* Menu Panel */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-72 overflow-y-auto"
        style={{
          background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
          boxShadow: '4px 0 20px rgba(0,0,0,0.5)'
        }}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold" style={{ color: '#ffd700' }}>Menu</h2>
            <button onClick={onClose} className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.1)' }}>
              <span className="text-white">✕</span>
            </button>
          </div>
          
          {/* Auth Buttons */}
          <div className="flex gap-2">
            <button 
              onClick={onSignUp}
              className="flex-1 py-2 rounded-lg font-bold text-sm"
              style={{
                background: 'linear-gradient(145deg, #ffd700, #b8860b)',
                color: '#1a1a2e'
              }}
            >
              {t('signUp')}
            </button>
            <button 
              onClick={onLogin}
              className="flex-1 py-2 rounded-lg font-bold text-sm"
              style={{
                border: '1px solid #ffd700',
                color: '#ffd700',
                background: 'transparent'
              }}
            >
              {t('login')}
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="p-4 border-b border-white/10">
          <h3 className="text-sm font-bold mb-3" style={{ color: '#888' }}>{t('statistics')}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <div className="text-[10px] uppercase" style={{ color: '#666' }}>{t('handsPlayed')}</div>
              <div className="text-2xl font-black" style={{ color: '#ffd700' }}>{handsPlayed}</div>
            </div>
            <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <div className="text-[10px] uppercase" style={{ color: '#666' }}>{t('netWinnings')}</div>
              <div className={`text-2xl font-black ${stats.winnings >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {stats.winnings >= 0 ? '+' : ''}{stats.winnings}
              </div>
            </div>
          </div>
          
          {/* Win/Lose/Fold bars */}
          <div className="mt-3 flex gap-2 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span style={{ color: '#888' }}>{stats.winPercent}% {t('win')}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
              <span style={{ color: '#888' }}>{stats.losePercent}% {t('lose')}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-gray-500"></div>
              <span style={{ color: '#888' }}>{stats.foldPercent}% Fold</span>
            </div>
          </div>
          
          <button 
            onClick={onStats}
            className="w-full mt-3 py-2 rounded-lg text-sm font-medium"
            style={{ background: 'rgba(255,215,0,0.1)', color: '#ffd700', border: '1px solid rgba(255,215,0,0.3)' }}
          >
            📊 {t('viewStats')}
          </button>
        </div>

        {/* Quick Actions */}
        <div className="p-4 border-b border-white/10">
          <h3 className="text-sm font-bold mb-3" style={{ color: '#888' }}>Actions</h3>
          <div className="space-y-2">
            <button 
              onClick={onReset}
              className="w-full py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              style={{ background: 'rgba(255,255,255,0.05)', color: '#ccc' }}
            >
              💰 {t('reloadChips')}
            </button>
            <button 
              className="w-full py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              style={{ background: 'rgba(255,255,255,0.05)', color: '#ccc' }}
            >
              🎰 {t('moreGames')}
            </button>
          </div>
        </div>

        {/* Payout Tables */}
        <div className="p-4">
          <h3 className="text-sm font-bold mb-3" style={{ color: '#888' }}>💎 {t('tripsPayout')}</h3>
          <div className="space-y-1 text-xs mb-4">
            {[
              ['Royal Flush', '50:1'],
              ['Straight Flush', '40:1'],
              ['Four of a Kind', '30:1'],
              ['Full House', '8:1'],
              ['Flush', '6:1'],
              ['Straight', '5:1'],
              ['Three of a Kind', '3:1'],
            ].map(([hand, payout]) => (
              <div key={hand} className="flex justify-between" style={{ color: '#aaa' }}>
                <span>{hand}</span>
                <span style={{ color: '#ffd700' }}>{payout}</span>
              </div>
            ))}
          </div>
          
          <h3 className="text-sm font-bold mb-3" style={{ color: '#888' }}>🎯 {t('blindPayout')}</h3>
          <div className="space-y-1 text-xs">
            {[
              ['Royal Flush', '500:1'],
              ['Straight Flush', '50:1'],
              ['Four of a Kind', '10:1'],
              ['Full House', '3:1'],
              ['Flush', '3:2'],
              ['Straight', '1:1'],
            ].map(([hand, payout]) => (
              <div key={hand} className="flex justify-between" style={{ color: '#aaa' }}>
                <span>{hand}</span>
                <span style={{ color: '#ffd700' }}>{payout}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
